import { yupResolver } from '@hookform/resolvers/yup';
import type { UserData } from '@eye-able/types';
import { useUpdateUser } from '@http/queries.js';
import { matchIsValidTel } from 'mui-tel-input';
import { Pencil } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import * as yup from 'yup';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  FormProvider,
  Input,
  PhoneInput,
  Select,
  SelectItem,
} from '@eye-able/ui';
import { useForm } from 'react-hook-form';
import LoadingButton from '@components/Button/LoadingButton';
import { noLeadingOrTrailingSpaces } from '@/helpers/yupValidation.js';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';

type ProfileEditDialogProps = {
  user?: UserData | null;
};

type ProfileFormValues = {
  email: string;
  firstName: string;
  lastName: string;
  mailLang: 'de' | 'en';
  phone: string;
};

const MAIL_LANG_OPTIONS = ['de', 'en'] as const;

export default function ProfileEditDialog({ user }: ProfileEditDialogProps) {
  const { i18n, t } = useInlineTranslation();
  const updateUserMutation = useUpdateUser();
  const updateUser = updateUserMutation.mutateAsync as unknown as (
    body: Record<string, unknown>,
  ) => Promise<unknown>;
  const [open, setOpen] = useState(false);
  const [dialogContentElement, setDialogContentElement] =
    useState<HTMLDivElement | null>(null);

  const schema = useMemo<yup.ObjectSchema<ProfileFormValues>>(
    () =>
      yup.object({
        firstName: noLeadingOrTrailingSpaces(t('firstName'))
          .max(128)
          .required(t('fieldRequiredDynamic', { fieldName: t('firstName') })),
        lastName: noLeadingOrTrailingSpaces(t('lastName'))
          .max(128)
          .required(t('fieldRequiredDynamic', { fieldName: t('lastName') })),
        email: yup
          .string()
          .max(128)
          .email(t('emailInvalid'))
          .required(t('emailRequired')),
        phone: yup
          .string()
          .test({
            name: 'valid-phone',
            message: t('validPhone'),
            test: (value) => !value || matchIsValidTel(value),
          })
          .default('')
          .defined(),
        mailLang: yup
          .mixed<ProfileFormValues['mailLang']>()
          .oneOf(MAIL_LANG_OPTIONS)
          .required(t('fieldRequiredDynamic', { fieldName: t('mailLang') })),
      }),
    [t],
  );

  const defaultValues = useMemo<ProfileFormValues>(
    () => ({
      email: user?.email?.toString() ?? '',
      firstName: user?.firstName?.toString() ?? '',
      lastName: user?.lastName?.toString() ?? '',
      mailLang: MAIL_LANG_OPTIONS.includes(user?.mailLang as 'de' | 'en')
        ? (user?.mailLang as ProfileFormValues['mailLang'])
        : i18n.resolvedLanguage === 'de'
          ? 'de'
          : 'en',
      phone: user?.phone?.toString() ?? '',
    }),
    [i18n.resolvedLanguage, user],
  );

  const form = useForm<ProfileFormValues>({
    defaultValues,
    resolver: yupResolver(schema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });
  const formControl = form.control as never;

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues, form, open]);

  const handleSubmit = async (values: ProfileFormValues) => {
    if (!user?.apiKey) {
      return;
    }

    // Only the fields this form edits: spreading the whole user record sent
    // the raw TinyInt columns back, which the API now rejects.
    const body = {
      modifyUserApiKey: user.apiKey,
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      email: values.email.trim(),
      phone: values.phone,
      mailLang: values.mailLang,
      primaryOrganisation: user.primaryOrganisation,
    };

    await updateUser(body);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        type='button'
        variant='outline'
        className='eye:w-full'
        disabled={!user?.apiKey}
        onClick={() => setOpen(true)}
      >
        <Pencil aria-hidden />
        {t('editProfile')}
      </Button>

      <DialogContent
        className='eye:w-full'
        ref={setDialogContentElement}
        maxWidth='md'
      >
        <DialogTitle>{t('editProfile')}</DialogTitle>

        <FormProvider {...form}>
          <form
            onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
          >
            <div className='eye:grid eye:gap-xs'>
              <FormField
                control={formControl}
                name='firstName'
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        {...field}
                        autoComplete='given-name'
                        label={t('firstName')}
                        required
                        aria-invalid={!!fieldState.error}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={formControl}
                name='lastName'
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        {...field}
                        autoComplete='family-name'
                        label={t('lastName')}
                        required
                        aria-invalid={!!fieldState.error}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={formControl}
                name='email'
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        {...field}
                        autoComplete='email'
                        type='email'
                        label={t('email')}
                        required
                        aria-invalid={!!fieldState.error}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={formControl}
                name='phone'
                render={({ field, fieldState }) => (
                  <FormItem className='eye:space-y-0'>
                    <PhoneInput
                      label={t('phone')}
                      ariaLabelNoCountry={t('noCountrySelected')}
                      value={field.value}
                      onChange={field.onChange}
                      error={!!fieldState.error}
                      popupContainer={dialogContentElement ?? undefined}
                      inputProps={{ name: field.name, maxLength: 20 }}
                    />
                    <FormMessage className='eye:mt-y-8' />
                  </FormItem>
                )}
              />

              <FormField
                control={formControl}
                name='mailLang'
                render={({ field, fieldState }) => (
                  <FormItem className='eye:space-y-0'>
                    <FormControl>
                      <Select
                        value={field.value}
                        onValueChange={(value) =>
                          field.onChange(value as ProfileFormValues['mailLang'])
                        }
                        label={t('mailLang')}
                        required
                        aria-invalid={!!fieldState.error}
                        slotProps={{
                          portal: {
                            container: dialogContentElement ?? undefined,
                          },
                        }}
                      >
                        <SelectItem value='de'>{t('de')}</SelectItem>
                        <SelectItem value='en'>{t('en')}</SelectItem>
                      </Select>
                    </FormControl>
                    <FormMessage className='eye:mt-y-8' />
                  </FormItem>
                )}
              />
            </div>

            <DialogActions>
              <Button
                type='button'
                variant='outline'
                disabled={updateUserMutation.isPending}
                onClick={() => setOpen(false)}
              >
                {t('cancel')}
              </Button>
              <LoadingButton
                type='submit'
                variant='yellow'
                loading={updateUserMutation.isPending}
              >
                {t('save')}
              </LoadingButton>
            </DialogActions>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
